import { Call, Device } from '@twilio/voice-sdk';

import type {
  SoftphoneCallHandlers,
  SoftphoneClient,
  SoftphoneDialParams,
  SoftphoneMediaStrategy,
} from './softphoneClient.types';

interface TwilioErrorInfo {
  code?: number;
  description?: string;
  explanation?: string;
  message?: string;
}

/**
 * Twilio Voice SDK errors arrive as `TwilioError` objects whose `message` is often the useless
 * generic "An unknown error has occurred. See error details for more information." The actionable
 * info lives in `code` + `description`/`explanation` (e.g. 31000 with an underlying 11200 HTTP
 * retrieval failure). Pull the structured fields out once so both the user-facing message and the
 * diagnostic log can use them.
 */
function twilioErrorInfo(e: unknown): TwilioErrorInfo {
  if (e == null || typeof e !== 'object') return {};
  const err = e as { code?: unknown; message?: unknown; description?: unknown; explanation?: unknown };
  return {
    ...(typeof err.code === 'number' ? { code: err.code } : {}),
    ...(typeof err.description === 'string' && err.description.length > 0 ? { description: err.description } : {}),
    ...(typeof err.explanation === 'string' && err.explanation.length > 0 ? { explanation: err.explanation } : {}),
    ...(typeof err.message === 'string' && err.message.length > 0 ? { message: err.message } : {}),
  };
}

/** Surface the code + the most specific text we have so the modal is debuggable. */
function errorMessage(e: unknown, fallback: string): string {
  const { code, description, explanation, message } = twilioErrorInfo(e);
  const detail = description || explanation || message || '';
  if (code != null && detail) return `${code}: ${detail}`;
  if (code != null) return `Error ${code}`;
  if (detail) return detail;
  return fallback;
}

/**
 * Log how a call ended so an instant hangup is diagnosable from the browser console (and any
 * console-capturing reporter) instead of vanishing into a benign "Call ended". A call that ends
 * before it was ever answered (`becameActive === false`) is the instant-hangup signature — that,
 * an `error` event, or any Twilio `code` is logged at error level with the correlation id; a
 * disconnect after the call was active is a normal hangup and logged at info level.
 */
function logCallTermination(event: string, voiceCallId: string, becameActive: boolean, e?: unknown): void {
  const info = twilioErrorInfo(e);
  const payload = { voiceCallId, event, becameActive, ...info };
  if (event === 'error' || info.code != null || !becameActive) {
    console.error('[softphone] call terminated abnormally', payload);
  } else {
    console.info('[softphone] call ended', payload);
  }
}

/**
 * Twilio implementation of the provider-agnostic softphone contract. Outbound calls go
 * through the TwiML App the AccessToken points at: `device.connect({ params })` sends our
 * `voiceCallId` to the server's TwiML endpoint, which resolves the real destination from
 * the voice_call row (the browser never names the number it dials).
 *
 * NOTE: the Twilio Device manages its own mic + audio playback, so the injectable
 * `SoftphoneMediaStrategy` (used by the extension's offscreen mic with Telnyx) is accepted
 * for interface parity but not yet honored here — extension + Twilio needs live validation.
 */
export class TwilioSoftphoneClient implements SoftphoneClient {
  private device: Device | null = null;
  private call: Call | null = null;
  private handlers: SoftphoneCallHandlers | null = null;
  /** Removes exactly the listeners we attached to the current call (set in `dial`). */
  private detachCallListeners: (() => void) | null = null;
  private readonly mediaStrategy?: SoftphoneMediaStrategy;

  constructor(mediaStrategy?: SoftphoneMediaStrategy) {
    this.mediaStrategy = mediaStrategy;
  }

  on(handlers: SoftphoneCallHandlers): void {
    this.handlers = handlers;
  }

  async init(token: string): Promise<void> {
    const existing = this.device;
    // Reuse the Device only while it's still REGISTERED. Destroying + re-registering the same
    // identity on every dial is what corrupts the 2nd/3rd consecutive call, so we keep the live
    // signaling connection and just refresh the (freshly minted) token. But a device that lost
    // its registration — e.g. superseded by another Device on the same `${userId}:${teamId}`
    // identity, or a network blip dropped it to Unregistered — would fail on `connect()`, so in
    // any non-Registered state we rebuild from scratch instead of reusing a dead connection.
    if (existing && existing.state === Device.State.Registered) {
      if (existing.token !== token) existing.updateToken(token);
      return;
    }
    // Non-reusable: release a dead-but-not-destroyed device before building a fresh one so we
    // don't leak its signaling resources (the original "destroy + recreate" leak in reverse).
    if (existing && existing.state !== Device.State.Destroyed) {
      try {
        existing.destroy();
      } catch {
        // already torn down
      }
    }
    const device = new Device(token, { logLevel: 'error' });
    device.on(Device.EventName.Error, (e: unknown) => {
      // Device-level errors (e.g. 31000 TwiML fetch failure, 31005 transport) are the ones that
      // silently kill an outbound call — log the structured code before surfacing the message.
      console.error('[softphone] device error', twilioErrorInfo(e));
      this.handlers?.onError(errorMessage(e, 'Voice connection error'));
    });
    this.device = device;
    // Registering isn't strictly required for outbound, but it surfaces token/connection
    // problems up front instead of on the first dial.
    await device.register();
  }

  async dial(params: SoftphoneDialParams): Promise<void> {
    const device = this.device;
    if (!device) throw new Error('Softphone is not initialized');

    // A half-open prior call must not bleed its events into this one.
    this.teardownCall();

    this.handlers?.onStatus('connecting');
    // Only the correlation id is sent; the destination is resolved server-side from the row.
    const call = await device.connect({ params: { voiceCallId: params.voiceCallId } });
    this.call = call;

    // Whether this call was ever answered — a terminal event that arrives before this flips true
    // is an instant hangup (the reported bug), which `logCallTermination` surfaces at error level.
    let becameActive = false;
    const onRinging = () => this.handlers?.onStatus('ringing');
    const onActive = () => {
      becameActive = true;
      this.handlers?.onStatus('active');
    };
    // disconnect/cancel/reject are all terminal; keep them distinct so the log names the exact
    // event (and so detach removes precisely what was attached).
    const makeTerminal = (event: 'disconnect' | 'cancel' | 'reject') => (arg?: unknown) => {
      logCallTermination(event, params.voiceCallId, becameActive, arg);
      this.handlers?.onStatus('ended');
      this.teardownCall();
    };
    const onDisconnect = makeTerminal('disconnect');
    const onCancel = makeTerminal('cancel');
    const onReject = makeTerminal('reject');
    const onError = (e: unknown) => {
      logCallTermination('error', params.voiceCallId, becameActive, e);
      this.handlers?.onError(errorMessage(e, 'Call error'));
      this.teardownCall();
    };

    call.on('ringing', onRinging);
    call.on('accept', onActive);
    call.on('reconnected', onActive);
    call.on('disconnect', onDisconnect);
    call.on('cancel', onCancel);
    call.on('reject', onReject);
    call.on('error', onError);

    // Detach exactly what we attached (not `removeAllListeners`, which would also strip the
    // SDK's internal listeners needed for clean signaling teardown).
    this.detachCallListeners = () => {
      call.off('ringing', onRinging);
      call.off('accept', onActive);
      call.off('reconnected', onActive);
      call.off('disconnect', onDisconnect);
      call.off('cancel', onCancel);
      call.off('reject', onReject);
      call.off('error', onError);
    };
  }

  hangup(): void {
    this.call?.disconnect();
  }

  setMuted(muted: boolean): void {
    this.call?.mute(muted);
  }

  closeCall(): void {
    this.teardownCall();
  }

  async destroy(): Promise<void> {
    this.teardownCall();
    try {
      this.device?.destroy();
    } catch {
      // already destroyed
    }
    this.device = null;
    return Promise.resolve();
  }

  /** Disconnect + fully detach the active call, leaving the Device registered for reuse. */
  private teardownCall(): void {
    const call = this.call;
    if (!call) return;
    // Detach first so the idempotent `disconnect()` can't re-enter our `onEnded` handler.
    this.detachCallListeners?.();
    this.detachCallListeners = null;
    try {
      call.disconnect();
    } catch {
      // already gone
    }
    this.call = null;
  }
}
